<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { SECTION_KEYS } from '@drama/novel-authoring'

const props = defineProps({
  /** 全部校验错误：`{ key, section_id, path, text }`。 */
  issues: { type: Array, default: () => [] },
  valid: { type: Boolean, default: false },
  summary_key: { type: String, default: 'novelStudio.editor.issuesTitle' },
})

const emit = defineEmits(['go'])
const { t } = useI18n()

/** 按分区分组，分区按导航顺序排。 */
const groups = computed(() => Object.keys(SECTION_KEYS)
  .map(section_id => ({ section_id, items: props.issues.filter(item => item.section_id === section_id) }))
  .filter(group => group.items.length > 0))
</script>

<template>
  <section
    v-if="issues.length"
    class="novel-issues"
    data-testid="novel-validation-issues"
  >
    <p
      class="novel-issues__summary"
      role="status"
    >
      {{ t(summary_key, { count: issues.length }) }}
    </p>
    <div class="novel-issues__groups">
      <section
        v-for="group in groups"
        :key="group.section_id"
        class="novel-issues__group"
      >
        <h2>{{ t(`novelStudio.sections.${group.section_id}`) }}</h2>
        <ul>
          <li
            v-for="item in group.items"
            :key="item.key"
          >
            <span>{{ item.text }}</span>
            <button
              type="button"
              :data-testid="`novel-issue-go-${item.key}`"
              @click="emit('go', item)"
            >
              {{ t('novelStudio.editor.goTo') }}
            </button>
          </li>
        </ul>
      </section>
    </div>
  </section>
  <p
    v-else-if="valid"
    class="novel-issues__valid"
    role="status"
  >
    {{ t('novelStudio.editor.valid') }}
  </p>
</template>

<style scoped>
.novel-issues,
.novel-issues__valid {
  margin: 0;
  padding: var(--drama-space-md) var(--drama-space-xl);
  border-bottom: 1px solid var(--drama-color-border);
  font-size: var(--drama-font-size-sm);
}

.novel-issues {
  display: grid;
  gap: var(--drama-space-sm);
  border-left: 3px solid var(--drama-color-danger);
  background: var(--drama-color-surface-alt);
}

.novel-issues__summary {
  margin: 0;
  color: var(--drama-color-danger);
  font-weight: var(--drama-font-weight-medium);
}

.novel-issues__groups {
  display: grid;
  gap: var(--drama-space-sm);
  max-height: calc(var(--drama-space-xl) * 6);
  overflow: auto;
}

.novel-issues__group h2 {
  margin: 0 0 var(--drama-space-xs);
  color: var(--drama-color-text);
  font-size: var(--drama-font-size-xs);
}

.novel-issues__group ul {
  display: grid;
  gap: var(--drama-space-xs);
  margin: 0;
  padding: 0;
  list-style: none;
}

.novel-issues__group li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--drama-space-md);
  color: var(--drama-color-text);
}

.novel-issues__group button {
  flex: none;
  padding: var(--drama-space-xs) var(--drama-space-md);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-sm);
  background: var(--drama-color-surface);
  color: var(--drama-color-text-muted);
  font: inherit;
  font-size: var(--drama-font-size-xs);
  cursor: pointer;
}

.novel-issues__group button:hover {
  color: var(--drama-color-text);
}

.novel-issues__group button:focus-visible {
  outline: 2px solid var(--drama-color-primary);
  outline-offset: 2px;
}

.novel-issues__valid {
  border-left: 3px solid var(--drama-color-success);
  color: var(--drama-color-text-muted);
}
</style>
